import React, { useEffect, useRef } from "react";
import type { DiagramCellConfig, DiagramLabels } from "@/lib/drawio/config";

/**
 * DiagramOverlay — the explanation card that opens on click, anchored to the
 * selected cell. Positioned in percent of the stage so it follows the SVG at
 * any width; below 760px the stylesheet turns it into a bottom sheet.
 * Flat card, navy border, one close control; Escape and a click on the
 * drawing background close it (handled by the island).
 */

export interface OverlayAnchor {
  /** Horizontal centre of the cell, in percent of the diagram width. */
  x: number;
  /** Top (placement above) or bottom (placement below) edge, in percent of the height. */
  y: number;
  placement: "above" | "below";
}

interface DiagramOverlayProps {
  id: string;
  cell: DiagramCellConfig;
  title: string;
  anchor: OverlayAnchor;
  labels: DiagramLabels;
  onClose: () => void;
}

export default function DiagramOverlay({ id, cell, title, anchor, labels, onClose }: DiagramOverlayProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    ref.current?.focus({ preventScroll: true });
  }, [id]);

  const style = {
    "--dg-ox": `${anchor.x}%`,
    "--dg-oy": `${anchor.y}%`,
  } as React.CSSProperties;

  return (
    <div
      ref={ref}
      className="dg-overlay"
      data-placement={anchor.placement}
      style={style}
      role="dialog"
      aria-modal="false"
      aria-labelledby={`${id}-title`}
      tabIndex={-1}
    >
      <div className="dg-overlay-head">
        <h4 id={`${id}-title`} className="dg-overlay-title">
          {title}
        </h4>
        <button type="button" className="dg-overlay-close" onClick={onClose} aria-label={labels.close}>
          ×
        </button>
      </div>
      <div className="dg-overlay-body" dangerouslySetInnerHTML={{ __html: cell.detail }} />
      {cell.href ? (
        <a className="dg-overlay-link" href={cell.href}>
          {cell.hrefLabel ?? labels.readOn}
        </a>
      ) : null}
    </div>
  );
}
